/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./ColorPicker.css";
import BgColorCheckIcon from "../components/icons/BgColorCheckIcon";

import { useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import styled, { css } from "styled-components";

let skipAddingToHistoryStack = false;

interface ColorPickerProps {
  color: string;
  onChange?: (value: string, skipHistoryStack: boolean) => void;
}

const basicColors = ["#ffffff", "#FFECEC", "#FFEFDB", "#FFFBBF", "#E7FFC9", "#D9FEFC", "#E5F4FF", "#F3ECFF", "#FFECFF"];

const checkIconColorMap = {
  "#ffffff": "#000000",
  "#FFECEC": "#E18787",
  "#FFEFDB": "#DDAD72",
  "#FFFBBF": "#CCC55C",
  "#E7FFC9": "#9FCB69",
  "#D9FEFC": "#72CBC6",
  "#E5F4FF": "#72CBC6",
  "#F3ECFF": "#B39ADE",
  "#FFECFF": "#DB94DB",
};

export default function BgColorPicker({ color, onChange }: Readonly<ColorPickerProps>): JSX.Element {
  const [selfColor, setSelfColor] = useState(transformColor("hex", color));
  const innerDivRef = useRef(null);

  useEffect(() => {
    // Check if the dropdown is actually active
    if (innerDivRef.current !== null && onChange) {
      onChange(selfColor.hex, skipAddingToHistoryStack);
    }
  }, [selfColor, onChange]);

  useEffect(() => {
    if (color === undefined) {
      return;
    }
    const newColor = transformColor("hex", color);
    setSelfColor(newColor);
  }, [color]);

  return (
    <Wrapper ref={innerDivRef}>
      {basicColors.map((basicColor) => (
        <ColorButtonWrapper>
          <ColorButton
            isWhite={basicColor === "#ffffff"}
            active={basicColor === selfColor.hex}
            key={basicColor}
            style={{
              backgroundColor: basicColor,
              border: `${basicColor === "#ffffff" ? "1px solid var(--gray4)" : "none"}`,
            }}
            onClick={() => {
              setSelfColor(transformColor("hex", basicColor));
            }}
          />
          {basicColor === selfColor.hex && basicColor !== "#ffffff" && <BgColorCheckIcon style={{ position: "absolute", right: 0, top: 0 }} color={checkIconColorMap[basicColor]} />}
        </ColorButtonWrapper>
      ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  width: 184px;
  padding: 8px 16px;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const ColorButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 24px;
  height: 24px;
`;

const ColorButton = styled.button<{ active: boolean; isWhite: boolean }>`
  /* s of reset button */
  background: inherit;
  border: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  outline-style: none;

  &:focus {
    outline: 0;
    border: none;
  }
  /* e of reset button  */

  ${({ isWhite }) =>
    isWhite &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: -8.6px;
        left: -0.3px;
        width: 137%;
        height: 137%;
        border-left: 1px solid #fc9495; /* Red diagonal line */
        transform: rotate(45deg);
        transform-origin: bottom left;
      }
    `}

  border-radius: 2px;
  height: 24px;
  width: 24px;
  cursor: pointer;
  list-style-type: none;
  background-color: ${({ active }) => (active ? "var(--SGray-Colors-gray-500)" : "transparent")};
  position: relative;
`;

export interface Position {
  x: number;
  y: number;
}

interface RGB {
  b: number;
  g: number;
  r: number;
}
interface HSV {
  h: number;
  s: number;
  v: number;
}
interface Color {
  hex: string;
  hsv: HSV;
  rgb: RGB;
}

export function toHex(value: string): string {
  if (!value.startsWith("#")) {
    const ctx = document.createElement("canvas").getContext("2d");

    if (!ctx) {
      throw new Error("2d context not supported or canvas already initialized");
    }

    ctx.fillStyle = value;

    return ctx.fillStyle;
  } else if (value.length === 4 || value.length === 5) {
    value = value
      .split("")
      .map((v, i) => (i ? v + v : "#"))
      .join("");

    return value;
  } else if (value.length === 7 || value.length === 9) {
    return value;
  }

  return "#000000";
}

function hex2rgb(hex: string): RGB {
  const rbgArr = (
    hex
      .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => "#" + r + r + g + g + b + b)
      .substring(1)
      .match(/.{2}/g) || []
  ).map((x) => parseInt(x, 16));

  return {
    b: rbgArr[2],
    g: rbgArr[1],
    r: rbgArr[0],
  };
}

function rgb2hsv({ r, g, b }: RGB): HSV {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);

  const h = d ? (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? 2 + (b - r) / d : 4 + (r - g) / d) * 60 : 0;
  const s = max ? (d / max) * 100 : 0;
  const v = max * 100;

  return { h, s, v };
}

function hsv2rgb({ h, s, v }: HSV): RGB {
  s /= 100;
  v /= 100;

  const i = ~~(h / 60);
  const f = h / 60 - i;
  const p = v * (1 - s);
  const q = v * (1 - s * f);
  const t = v * (1 - s * (1 - f));
  const index = i % 6;

  const r = Math.round([v, q, p, p, t, v][index] * 255);
  const g = Math.round([t, v, v, q, p, p][index] * 255);
  const b = Math.round([p, p, t, v, v, q][index] * 255);

  return { b, g, r };
}

function rgb2hex({ b, g, r }: RGB): string {
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

function transformColor<M extends keyof Color, C extends Color[M]>(format: M, color: C): Color {
  let hex: Color["hex"] = toHex("#121212");
  let rgb: Color["rgb"] = hex2rgb(hex);
  let hsv: Color["hsv"] = rgb2hsv(rgb);

  if (format === "hex") {
    const value = color as Color["hex"];

    hex = toHex(value);
    rgb = hex2rgb(hex);
    hsv = rgb2hsv(rgb);
  } else if (format === "rgb") {
    const value = color as Color["rgb"];

    rgb = value;
    hex = rgb2hex(rgb);
    hsv = rgb2hsv(rgb);
  } else if (format === "hsv") {
    const value = color as Color["hsv"];

    hsv = value;
    rgb = hsv2rgb(hsv);
    hex = rgb2hex(rgb);
  }

  return { hex, hsv, rgb };
}
